import type { VisitStatus, ConsentState } from '@assistmd/types';
import { StatusBadge } from '@/components/ui/StatusBadge';

interface VisitStateCardProps {
  status: VisitStatus;
  consentState: ConsentState;
  isRecording: boolean;
  totalRecordingMs: number;
  segmentCount: number;
  wordCount: number;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Consent badge color mapping.
 * granted = emerald, revoked = red, unknown/expired = slate/amber.
 */
function getConsentBadgeClasses(consent: ConsentState): string {
  switch (consent) {
    case 'granted':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200/60';
    case 'revoked':
      return 'bg-red-50 text-red-600 border border-red-200/60';
    case 'expired':
      return 'bg-amber-50 text-amber-700 border border-amber-200/60';
    case 'unknown':
    default:
      return 'bg-slate-100 text-slate-500 border border-slate-200/60';
  }
}

/**
 * Status visual indicator -- small colored line segment.
 */
function getStatusIndicatorColor(status: VisitStatus): string {
  switch (status) {
    case 'recording':
      return 'bg-red-500';
    case 'finalizing':
      return 'bg-amber-500';
    case 'draft':
      return 'bg-slate-400';
    case 'closed':
      return 'bg-slate-500';
    default:
      return 'bg-slate-400';
  }
}

/**
 * Visit state summary card -- premium layout with grid fields,
 * .overline labels, recording-pulse indicator, colored consent badge,
 * status transition indicator, and metric cards with serif values.
 */
export function VisitStateCard({
  status,
  consentState,
  isRecording,
  totalRecordingMs,
  segmentCount,
  wordCount,
}: VisitStateCardProps): JSX.Element {
  return (
    <div className="card animate-fade-in p-5">
      <h3 className="section-header mb-5">Visit State</h3>

      {/* State fields -- grid layout with overline labels */}
      <div className="space-y-4">
        {/* Status row */}
        <div className="grid grid-cols-[1fr_auto] items-center gap-4 animate-stagger-1">
          <div>
            <span className="overline">Status</span>
            <div className="mt-1.5 flex items-center gap-2">
              {/* Status transition indicator bar */}
              <span className={`h-5 w-[3px] rounded-full ${getStatusIndicatorColor(status)}`} />
              <StatusBadge status={status} kind="visit" size="md" />
            </div>
          </div>
        </div>

        {/* Consent row */}
        <div className="grid grid-cols-[1fr_auto] items-center gap-4 animate-stagger-2">
          <div>
            <span className="overline">Consent</span>
            <div className="mt-1.5">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${getConsentBadgeClasses(consentState)}`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    consentState === 'granted'
                      ? 'bg-emerald-500'
                      : consentState === 'revoked'
                        ? 'bg-red-500'
                        : consentState === 'expired'
                          ? 'bg-amber-600'
                          : 'bg-slate-400'
                  }`}
                />
                {consentState.charAt(0).toUpperCase() + consentState.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Recording row */}
        <div className="grid grid-cols-[1fr_auto] items-center gap-4 animate-stagger-3">
          <div>
            <span className="overline">Recording</span>
            <div className="mt-1.5 flex items-center gap-2.5">
              {isRecording && (
                <span className="recording-pulse" />
              )}
              <span className="font-mono text-sm font-medium text-slate-700 tabular-nums">
                {formatDuration(totalRecordingMs)}
              </span>
              {isRecording && (
                <span className="badge-red text-2xs font-bold uppercase tracking-wider">
                  Live
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Gradient divider */}
        <div className="divider-gradient" />

        {/* Metrics -- grid of metric-cards */}
        <div className="grid grid-cols-2 gap-3 animate-stagger-4">
          <div className="metric-card text-center">
            <p className="metric-value">
              {segmentCount.toLocaleString()}
            </p>
            <p className="metric-label mt-1">Segments</p>
          </div>
          <div className="metric-card text-center">
            <p className="metric-value">
              {wordCount.toLocaleString()}
            </p>
            <p className="metric-label mt-1">Words</p>
          </div>
        </div>
      </div>
    </div>
  );
}
